

export type PositionPoint = {
  date: string;
  position: number;
  measuredAt?: string;
};

export type KeywordEntry = {
  keyword: string;
  dailyClicks?: number;
  position?: number | null;
  positionHistory?: PositionPoint[];
  ok?: number;
  failed?: number;
  running?: number;
  lastQuery?: string | null;
};

export type PfTask = {
  id: string;
  site: string;
  url?: string;
  scenario?: string;
  proxyTag?: string;
  lane?: 'pf' | 'neuro';
  searchEngine?: 'bing' | 'google';
  clickProfile?: 'warmed' | 'empty';
  selector?: string;
  status: string;
  keywords?: KeywordEntry[];
  perDay?: number;
  dailyClicks?: number;
  clicksQueued?: number;
  clicksRunning?: number;
  clicksOk?: number;
  clicksFailed?: number;
  clicksMiss?: number;
  clicksError?: number;
  quotaBaselineOk?: number;
  progressPct?: number;
  createdAt?: string;
  updatedAt?: string;
};

export function keywordHasHostToken(keyword: string): boolean {
  const text = String(keyword || '').toLowerCase();
  return /[a-z0-9-]+\.[a-z]{2,}/i.test(text) || text.includes('site:');
}

export function isRankingKeyword(keyword: string): boolean {
  return Boolean(keyword.trim()) && !keywordHasHostToken(keyword);
}

export function sameSerpQuery(keyword: string, query?: string | null): boolean {
  const k = keyword.trim().toLowerCase().replace(/\s+/g, ' ');
  const q = String(query || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!k) return false;
  if (!q) return true;
  return q === k;
}

export type PositionStats = {
  current: number | null;
  previous: number | null;
  delta: number | null;
  recent: PositionPoint[];
};

export type PositionSort = 'pos' | 'clicks' | 'searches' | 'delta' | 'drop' | 'day';

const DAY_MS = 86_400_000;

export function positionStats(keyword: KeywordEntry, now: number = Date.now()): PositionStats {
  const trusted = isRankingKeyword(keyword.keyword || '') && sameSerpQuery(keyword.keyword || '', keyword.lastQuery);
  const today = new Date(now).toISOString().slice(0, 10);
  const week = new Date(now - 7 * DAY_MS).toISOString().slice(0, 10);
  const start = new Date(now - 13 * DAY_MS).toISOString().slice(0, 10);
  const history = trusted
    ? (keyword.positionHistory || [])
      .filter((p) => p.date <= today && Number.isFinite(p.position) && p.position > 0)
      .sort((a, b) => a.date.localeCompare(b.date))
    : [];
  const pos = trusted ? keyword.position : null;
  const current = typeof pos === 'number' && Number.isFinite(pos) && pos > 0 ? pos : null;
  const previous = history.find((p) => p.date === week)?.position ?? null;
  const delta = current !== null && previous !== null ? previous - current : null;
  const recent = history.filter((p) => p.date >= start);
  return { current, previous, delta, recent };
}


export function taskDailyClicks(task: Pick<PfTask, 'dailyClicks' | 'perDay' | 'keywords'> | null | undefined): number {
  const direct = Math.floor(Number(task?.dailyClicks ?? task?.perDay) || 0);
  if (direct > 0) return direct;
  return (task?.keywords || []).reduce((s, k) => s + Math.floor(Number(k.dailyClicks) || 0), 0);
}


export function keywordSearches(keyword: Pick<KeywordEntry, 'ok' | 'failed'> | null | undefined): number {
  return Math.floor(Number(keyword?.ok) || 0) + Math.floor(Number(keyword?.failed) || 0);
}

export function keywordClicks(keyword: Pick<KeywordEntry, 'ok'> | null | undefined): number {
  return Math.floor(Number(keyword?.ok) || 0);
}

export function comparePositions(
  a: KeywordEntry,
  b: KeywordEntry,
  sort: string,
  _taskA?: PfTask,
  _taskB?: PfTask,
): number {
  const left = positionStats(a);
  const right = positionStats(b);
  if (sort === 'day' || sort === 'clicks') {
    const clickDiff = keywordClicks(b) - keywordClicks(a);
    if (clickDiff) return clickDiff;
    return keywordSearches(b) - keywordSearches(a);
  }
  if (sort === 'searches') return keywordSearches(b) - keywordSearches(a);
  if (sort === 'delta' || sort === 'drop') {
    if (left.delta === null) return right.delta === null ? 0 : 1;
    if (right.delta === null) return -1;
    return sort === 'delta' ? right.delta - left.delta : left.delta - right.delta;
  }
  return (left.current ?? Infinity) - (right.current ?? Infinity);
}

export type KeywordRow = { k: KeywordEntry; t: PfTask };


export function allKeywordRows(tasks: PfTask[]): KeywordRow[] {
  return tasks.flatMap((t) => (t.keywords || []).map((k) => ({ k, t })));
}

export function formatDelta(delta: number | null): string {
  if (delta === null) return '—';
  return `${delta > 0 ? '+' : ''}${delta}`;
}
